// Connectivity plugin.
import { openWebsocket, cleanupWebSocket, wsEmitter } from './websocket'
import { getConnectivityConfig, getPingInterval, getCheckConnectivity } from '../interface/selectors'
import * as actionTypes from '../interface/actionTypes'
import * as actions from '../interface/actions'

// Other plugins.
import { getConnectionInfo } from '../../auth/interface/selectors'
import { getSubscriptionInfo } from '../../subscription/interface/selectors'
import { logManager } from '../../logs'

// Libraries.
import { takeEvery, delay, call, put, fork, cancel, actionChannel, race, select, take } from 'redux-saga/effects'

// Constants
import { platforms, connCheckMethods, connCheckResponsibility } from '../../constants'

// Helpers
import BasicError from '../../errors'
import { subscriptionCodes, connectivityCodes } from '../../errors/codes'

// Get the logger
const log = logManager.getLogger('CONNECTIVITY')

export function * wsConnectFlow () {
  log.info('Creating channel for handling websocket actions ...')
  const chan = yield actionChannel(actionTypes.WS_ATTEMPT_CONNECT)
  log.info('Channel successfully created.')
  yield takeEvery(chan, websocketLifecycle)
}

/**
 * Saga that handles a websocket over its lifecycle.
 * @method websocketLifecycle
 * @param  {Object} wsConnectAction
 */
export function * websocketLifecycle (wsConnectAction) {
  const wsInfo = wsConnectAction.payload
  const { platform, isReconnect } = wsConnectAction.meta

  // Redux-saga take() pattern.
  // Take disconnect websocket action for this platform.
  function disconnectWebsocketPattern (action) {
    return action.type === actionTypes.WS_DISCONNECT && action.meta.platform === platform
  }

  log.info(`Connecting to websocket on platform: ${platform} ...`)
  // Try to open the websocket, but cancel if we get a disconnect action.
  const { websocket, disconnect } = yield race({
    websocket: call(connectWebsocket, wsInfo, platform),
    disconnect: take(disconnectWebsocketPattern)
  })

  // If the websocket didn't open, stop here. Dispatch the error if there was one.
  if (disconnect) {
    log.info('Received disconnect during websocket connection; stopping.')
    return
  } else if (websocket.error) {
    if (isReconnect) {
      yield put(actions.wsReconnectFailed(undefined, platform))
      return
    } else {
      yield put(
        actions.wsConnectFinished(
          new BasicError({
            message: websocket.message,
            code: subscriptionCodes.WS_CONNECTION_ERROR
          }),
          platform
        )
      )
      return
    }
  }

  // Append information to the websocket, so that its accessible elsewhere.
  // TODO: Remove this and replace with proper redux state storage.
  const connConfig = yield select(getConnectivityConfig)
  websocket.kandy = connConfig

  websocket.kandy.wsInfo = wsInfo
  websocket.kandy.platform = platform

  // set last contact in both cases to be now
  websocket.kandy.lastContact = Date.now()

  const emitTask = yield fork(wsEmitter, websocket, platform)

  // determine which pingFlow is appropriate
  let pingFlow
  if (websocket.kandy.method.responsibleParty === connCheckResponsibility.SERVER) {
    log.debug(`Starting a serverPing flow on ${platform} ...`)
    pingFlow = yield fork(serverPingFlow, websocket, platform)
  } else {
    log.debug(`Starting a clientPing flow on ${platform} ...`)
    pingFlow = yield fork(clientPingFlow, websocket, platform)
  }

  // Dispatch the finish action, to notify that the websocket is connected.
  yield put(actions.wsConnectFinished(websocket, platform))

  // Redux-saga take() pattern.
  // Take end of lifecycle WS actions for this platform.
  function closeWebsocketPattern (action) {
    return (
      (action.type === actionTypes.WS_DISCONNECT ||
        action.type === actionTypes.LOST_CONNECTION ||
        action.type === actionTypes.WS_CLOSED ||
        action.type === actionTypes.WS_ERROR) &&
      action.meta.platform === platform
    )
  }

  // Wait for a disconnect or lost connection action.
  const action = yield take(closeWebsocketPattern)
  log.debug(`Cancelling outstanding tasks upon receiving action: ${action.type}`)

  // Whether we're disconnecting or have lost connection,
  //      we want to cancel these tasks either way.
  yield cancel([emitTask, pingFlow])

  log.debug('Cleaning up websocket connection ...')
  yield call(cleanupWebSocket, websocket)

  if (action.type === actionTypes.WS_DISCONNECT) {
    // If we're disconnecting, close the websocket to end it's lifecycle.
    yield put(actions.wsDisconnectFinished(undefined, platform))
    log.info('Successfully disconnected websocket connection.')
  } else if (action.type === actionTypes.WS_CLOSED && action.payload.code === 1000) {
    // 1000 signifies a normal closure
    // Normal websocket close, don't try to reconnect
    log.debug('Websocket closed normally.')
    yield put(actions.wsReconnectFailed(undefined, platform))
  } else {
    if (websocket.kandy.autoReconnect) {
      // If this is a Link websocket, we need to ensure the URL is using the
      //     "latest" access token from state.
      if (wsConnectAction.meta.platform === platforms.UC) {
        log.info('Updating access token ...')
        const { notificationChannel } = yield select(getSubscriptionInfo)
        const { accessToken, oauthToken } = yield select(getConnectionInfo)
        wsInfo.url = notificationChannel
        if (oauthToken && !accessToken) {
          wsInfo.params = {
            access_token: oauthToken
          }
        } else {
          wsInfo.params = {
            token: accessToken
          }
        }
      } else if (wsConnectAction.meta.platform === platforms.LINK) {
        const { bearerAccessToken, accessToken } = yield select(getConnectionInfo)
        // If using bearerAccessToken and WS query mode, set the token as part
        //    of the WS reconnect query string.
        if (bearerAccessToken && connConfig.webSocketOAuthMode === 'query') {
          wsInfo.params = {
            access_token: bearerAccessToken
          }
        } else if (accessToken) {
          wsInfo.params = {
            token: accessToken
          }
        }
      }

      // If we've lost connection, re-dispatch the initial action, so that we can
      //      start the lifecycle over.
      log.debug('Attempting to reconnect using a new websocket ...')
      yield put(actions.wsAttemptConnect(wsInfo, wsConnectAction.meta.platform, true))
    } else {
      log.debug('Not attempting to reconnect to websocket autoReconnect is false.')
      yield put(actions.wsReconnectFailed(undefined, platform))
    }
  }
}

/**
 * if we receive a server ping we want to respond with a pong,
 * if there is none we want to check to see if its been too long since the server last pinged us and if it has been then reconnect,
 * otherwise we just want to increment the time since the last ping
 * @param {Object} ws a websocket connected to the backend
 * @return {Object} yields a Flux standard action
 */
export function * serverPingFlow (ws) {
  const { lastContact, platform, maxMissedPings, autoReconnect } = ws.kandy
  let timeOfLastPing = Date.now()
  const { pingInterval: configPingInterval } = yield select(getConnectivityConfig)
  if (configPingInterval) {
    log.warn(
      '`connectivity.pingInterval` has been set, but `connectivity.method.responsibleParty` is set to `server`. ' +
        `The configured value of ${configPingInterval}ms will be ignored.`
    )
  }

  while (true) {
    /**
     * //TODO: Get rid of this use of getPingInterval
     * The server doesn't even use this interval, it uses whatever the server is set to
     * It gets an undefined value, sets it to 120 seconds, and when the server responds switched to that timeout.
     * See KJS-58 for a discussion of the problem, and KAA-2215 for a long-term solution
     */
    let pingInterval = yield select(getPingInterval)
    pingInterval = typeof pingInterval !== 'undefined' ? pingInterval : 120000
    const maxIdleDuration = pingInterval * maxMissedPings
    log.debug(`serverPing: Using ping interval: ${pingInterval} with maximum idle duration: ${maxIdleDuration}`)

    // wait for incoming server pings or disconnect actions on an interval
    const { serverPing, disconnect } = yield race({
      expiry: delay(pingInterval),
      serverPing: take(actionTypes.RECEIVE_SERVER_PING),
      disconnect: take(actionTypes.WS_DISCONNECT_FINISHED)
    })

    // is disconnect action received then exit
    if (disconnect) {
      log.debug('Got disconnect action. Exiting from serverPing flow ...')
      break
    }

    if (serverPing) {
      // server sent us a ping so reset the timeSinceLastPing
      timeOfLastPing = Date.now()

      // pingInterval is stored in milliseconds but comes in as seconds so convert
      const pingIntervalMillis = serverPing.payload.connCheck.interval * 1000
      // Prevent firing actions if pingInterval hasn't changed
      if (pingIntervalMillis !== pingInterval) {
        yield put(actions.changePingInterval(pingIntervalMillis, platform))
      }

      const message = { connAck: {} }
      log.debug(
        `Received a server ping which uses interval: ${pingIntervalMillis} ms. ${platform} is replying with message: ${message}`
      )
      const error = _sendWSMessage(ws, JSON.stringify(message))

      // if the pong websocket message has an error then try to reconnect
      if (error) {
        log.info(`Got error when attempting to reply: ${error.message}`)
        if (autoReconnect) {
          log.info('Trying to auto reconnect ...')
        }
        yield put(actions.lostConnection(undefined, platform))
        break
      }
    } else {
      if (Date.now() - timeOfLastPing >= maxIdleDuration) {
        log.warn('Closing websocket due to inactivity. (have not received pong from server)', platform)

        // try to reconnect or exit
        if (autoReconnect) {
          log.debug(`${platform} is attempting to auto reconnect ...`)
        }
        yield put(actions.lostConnection(undefined, platform))
        break
      } else {
        log.debug(`${platform} websocket last contact: ${lastContact}. Reconnect after ${maxIdleDuration}.`)
      }
    }
  }
}

/**
 * This flow is responsible for handling keepAlive and pingPong methods for client,
 * if keepAlive is used the client simply sends the keepAlive message to the websocket after waiting the pingInterval
 * if pingPong is used the client is responsible for pinging the server and listening for server "pong" responses via the websocket
 * the client will then determine if too much time has passed and attempt to reconnect if its not receiving server pongs or if an error occurs
 * @param {Object} ws a websocket connected to the backend
 * @return {Object} yields a Flux standard action
 */
export function * clientPingFlow (ws) {
  const { lastContact, platform, maxMissedPings, autoReconnect } = ws.kandy
  const { checkConnectivity, method } = yield select(getConnectivityConfig)

  let timeOfLastPong = Date.now()
  let message = ''
  let shouldCheck

  while (true) {
    // If the flag was explicitly set in state, prefer that over the config.
    shouldCheck = yield select(getCheckConnectivity)
    shouldCheck = typeof shouldCheck !== 'undefined' ? shouldCheck : checkConnectivity

    const { pingInterval } = yield select(getConnectivityConfig)
    const maxIdleDuration = pingInterval * maxMissedPings
    const intervalInSeconds = pingInterval / 1000

    if (shouldCheck) {
      if (method.type === connCheckMethods.PING_PONG) {
        message = { connCheck: { interval: intervalInSeconds } }
      } else if (method.type === connCheckMethods.KEEP_ALIVE) {
        message = { message_type: 'ping' }
      } else {
        log.error(`Invalid connectivity method: ${method}`)
        break
      }

      log.debug(`${platform} is sending a ${method.type} every ${intervalInSeconds} sec.`)

      const error = _sendWSMessage(ws, JSON.stringify(message))
      if (error) {
        log.error('Exception in clientPing flow: ' + error.message)
        if (autoReconnect) {
          log.debug(`${platform} is attempting to auto reconnect ...`)
        }
        yield put(actions.lostConnection(undefined, platform))
        break
      }
    } else {
      log.debug('Set to not check websocket connectivity. Waiting for connectivity status change ...')

      // If we shouldn't ping, wait until we receive a trigger to (maybe) ping.
      const shouldCheckConnectivity = yield take(
        action => action.type === 'CHANGE_CONNECTIVITY_CHECKING' && action.payload
      )
      log.debug(`Connectivity check setting changed. Check connectivity?: ${shouldCheckConnectivity}`)
      continue
    }

    const delayStartTime = Date.now()
    // Wait for either the ping interval, a disconnect or a pong from the server
    const { disconnect, serverPong } = yield race({
      expiry: delay(pingInterval),
      serverPong: take(actionTypes.RECEIVE_SERVER_PONG),
      disconnect: take(actionTypes.WS_DISCONNECT_FINISHED)
    })

    // If we receive a serverPong, we will not have waited the full pingInterval duration.
    //    Determine how long we were waiting for, so that we know how long to wait (again)
    //    to fulfill the full pingInterval duration (before restarting the ping loop).
    const timeElapsed = Date.now() - delayStartTime

    // If we received a disconnect action, stop the pings and exit.
    if (disconnect) {
      log.debug('Got disconnect action. Exiting from clientPing flow ...')
      break
    }

    if (method.type === connCheckMethods.PING_PONG) {
      if (serverPong) {
        // we received a pong, wait pingInterval to send another ping
        timeOfLastPong = Date.now()

        const timeUntilNextPing = pingInterval - timeElapsed
        const { disconnect } = yield race({
          expiry: delay(timeUntilNextPing),
          disconnect: take(actionTypes.WS_DISCONNECT_FINISHED)
        })
        // If we received a disconnect action, stop the pings and exit.
        if (disconnect) {
          log.debug('Got disconnect action. Exiting from clientPing flow ...')
          break
        }
      } else {
        if (Date.now() - timeOfLastPong >= maxIdleDuration) {
          log.warn('Closing websocket due to inactivity. (have not received pong from server)', platform)

          // its been too long since the last pong, attempt to reconnect or exit
          yield put(actions.lostConnection(undefined, platform))

          break
        } else {
          log.debug(`${platform} websocket last contact: ${lastContact}. Reconnect after ${maxIdleDuration}.`)
        }
      }
    }
  }
}

/**
 * Generic Websocket message that takes a websocket and a message and sends it
 * @param  {Object} ws  websocket
 * @return {Object}     Error object
 */
function _sendWSMessage (ws, message) {
  try {
    if (ws && ws.readyState === 1) {
      log.debug('Sending message on websocket.', message)
      ws.send(message)
    } else {
      throw new BasicError({
        message: 'websocket was not in readyState',
        code: connectivityCodes.WS_MESSAGE_ERROR
      })
    }
  } catch (e) {
    return e
  }
}

/**
 * Helper function for connecting to a websocket.
 * Attempts to connect a specified number of times before returning an error.
 * Includes a delay in between attempts, determined by configs.
 * @method connectWebsocket
 * @param  {Object} wsInfo
 * @return {Websocket|Object}  Either a connected websocket or an error object.
 */
export function * connectWebsocket (wsInfo, platform) {
  const configs = yield select(getConnectivityConfig)
  let connectionAttempt = 0
  // The delay between attempts should not be shorter than 2 seconds.
  let delayTime = configs.reconnectDelay > 2000 ? configs.reconnectDelay : 2000
  let websocket

  // If no limit is set, we will continually attempt to reconnect.
  if (!configs.reconnectLimit) {
    log.debug('No connectivity reconnect limit set.')
  }

  function * safeOpenWs (wsInfo) {
    let websocket
    try {
      websocket = yield call(openWebsocket, wsInfo)
    } catch (err) {
      websocket = err
    }
    return websocket
  }

  while (connectionAttempt < configs.reconnectLimit || !configs.reconnectLimit) {
    const wsConnectStart = Date.now()
    const { openWs, timeout } = yield race({
      openWs: call(safeOpenWs, wsInfo),
      timeout: delay(delayTime)
    })
    const attemptDuration = Date.now() - wsConnectStart

    // Checking for both timeout and open websocket errors here since we need to calculate the next delay parameters in
    //  both scenarios
    if (timeout || (openWs && openWs.error)) {
      connectionAttempt++
      websocket = openWs
      log.debug(
        `Failed to connect to websocket on ${platform}. (Attempt #${connectionAttempt}). Message: ${
          timeout ? 'Timed out' : websocket.message
        }.`
      )

      // If we are still under the reconnect attempt limit, calculate the next delay time and delay before retrying.
      if (connectionAttempt < configs.reconnectLimit || !configs.reconnectLimit) {
        // Calculate the remaining delay time by checking how long the previous connection attempt was.
        // Do this before potentially lengthening the `delayTime` value.
        const remainingDelay = timeout ? 0 : delayTime - attemptDuration

        // Increase the delay time for the next loop if we're not at the limit.
        if (delayTime !== configs.reconnectTimeLimit) {
          delayTime = configs.reconnectDelay * Math.pow(configs.reconnectTimeMultiplier, connectionAttempt)
          delayTime = delayTime < configs.reconnectTimeLimit ? delayTime : configs.reconnectTimeLimit
        }

        log.debug(`Websocket will attempt to reconnect after ${remainingDelay} ms on ${platform}`)
        if (remainingDelay > 0) {
          yield delay(remainingDelay)
        }
      } else {
        log.debug(`Stopping websocket connection attempts on ${platform}.`)
        // We are at reconnect attempt limit; if it was due to a timeout we need to return an error. In case of
        //  websocket error, that will be returned by the websocket.
        if (timeout) {
          return {
            error: true,
            message: `Websocket connection timed out. Tried ${connectionAttempt} times to connect.`
          }
        }
        break
      }
    } else if (openWs) {
      websocket = openWs
      break
    }
  }

  // Return either the websocket or the latest error.
  return websocket
}
